import type { PageNode } from "@baze/schema";

/**
 * Tiny inline enhancer for invitation-style pages (no external file, no deps). It only runs where the
 * page needs it (see `needsInvitationScript`):
 * - fills every [data-baze-guest] with the guest name from `?to=` (or `?kepada=`), so one published
 *   page serves personal links ("Kepada Yth. Budi"). textContent only: the name is never parsed as HTML;
 * - ticks every [data-baze-countdown="<epoch ms>"] once per second (the server render is the fallback)
 *   and swaps to its [data-baze-countdown-done] text when the moment has passed. The timer stops once
 *   those nodes leave the document (a client navigation away from a demo);
 * - plays invitation openings: a click on a `[data-baze-open]` in-page link sets `data-opened` on the
 *   cover's `[data-baze-gate]` (CSS breaks the seal, tears the ticket…) and follows the link once the
 *   opening has played (at once for reduced motion). Without the script the link is a plain anchor.
 */
const GUEST_NAME = `try{var q=new URLSearchParams(location.search);g=(q.get("to")||q.get("kepada")||"").trim().slice(0,60)}catch(e){}`;

/**
 * The enhancer. `guestLinks` false (the site's plan has no personal links) leaves the placeholder name.
 */
export function invitationScript(guestLinks: boolean): string {
  return INVITATION_SCRIPT.replace(GUEST_NAME, guestLinks ? GUEST_NAME : "");
}

export const INVITATION_SCRIPT =
  // Runs after load (and re-applies the name shortly after): React hydration restores server text, so
  // touching the DOM earlier would be undone. Safe to run again on new DOM (editor preview): the click
  // handler is added once per window and one countdown timer re-queries the nodes on every tick.
  `(function(w,d){var g="";` +
  GUEST_NAME +
  `function f(){g&&d.querySelectorAll("[data-baze-guest]").forEach(function(e){e.textContent!==g&&(e.textContent=g)})}` +
  `function p(n){return n<10?"0"+n:""+n}` +
  `function k(){var c=d.querySelectorAll("[data-baze-countdown]");if(!c.length)return clearInterval(w.__bazeCd);var n=Date.now();c.forEach(function(e){var t=Math.max(0,Math.floor((+e.getAttribute("data-baze-countdown")-n)/1e3)),` +
  `v={d:Math.floor(t/86400),h:p(Math.floor(t%86400/3600)),m:p(Math.floor(t%3600/60)),s:p(t%60)},o=e.querySelector("ol"),x=e.querySelector("[data-baze-countdown-done]");` +
  `e.querySelectorAll("[data-unit]").forEach(function(u){u.textContent=v[u.getAttribute("data-unit")]});` +
  `if(o)o.hidden=!t;if(x)x.hidden=!!t})}` +
  `w.__bazeInv||(w.__bazeInv=1,d.addEventListener("click",function(e){var a=e.target.closest&&e.target.closest("[data-baze-open] a[href^='#']"),h=d.querySelector("[data-baze-gate]");` +
  `if(a&&h){e.preventDefault();h.setAttribute("data-opened","");setTimeout(function(){var t=d.getElementById(a.hash.slice(1));t&&t.scrollIntoView({behavior:"smooth"})},matchMedia("(prefers-reduced-motion:reduce)").matches?0:1400)}}));` +
  `function s(){f();setTimeout(f,600);setTimeout(f,2000);clearInterval(w.__bazeCd);if(d.querySelector("[data-baze-countdown]")){k();w.__bazeCd=setInterval(k,1e3)}}` +
  `d.readyState=="complete"?s():w.addEventListener("load",s)})(window,document)`;

/** Hero variants that are invitation covers (guest name, opening). */
const COVER_VARIANTS = new Set(["cover", "envelope", "boarding-pass", "newspaper", "glasshouse"]);

/** Pages with a countdown or an invitation cover get the script; everything else ships none. */
export function needsInvitationScript(page: Pick<PageNode, "sections">): boolean {
  return page.sections.some(
    (s) => !s.hidden && (s.type === "countdown" || (s.type === "hero" && COVER_VARIANTS.has(s.variant))),
  );
}
